import React, { useEffect, useState } from 'react';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Pagination from '@mui/material/Pagination';
import { useMyContext } from '../../../context/MyContext';
import { FaCcVisa, FaCcMastercard, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFilter, FaTimes, FaCheckCircle } from "react-icons/fa";
import PaginationItem from '@mui/material/PaginationItem';
import { Base_Url, topUpList, userTrnxList } from '../../../config/config';

const B2bTopupHistory = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const cus_id = localStorage.getItem('cus_id');
  const [totalOrders, setTotalOrders] = useState(0);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [historyData, setHistoryData] = useState([]);
  const [allHistoryData, setAllHistoryData] = useState([]); // Store all data for client-side filtering
  const navigate = useNavigate();

  // Add date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const handleNavigation = () => {
    navigate('b2b/dashboard');
  };

  const getTopupHistory = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=${page}&limit=${limit}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.status === 'SUCCESS') {
        const transactions = data.data;

        if (isDateFilterActive) {
          // Apply client-side filtering based on dates
          const filtered = applyDateFilter(transactions);
          setHistoryData(filtered);
          setTotalOrders(filtered.length);
        } else {
          setHistoryData(transactions);
          setTotalOrders(data.pagination.totalItems || 0);
        }

        // Store all data for filtering
        setAllHistoryData(prev => {
          // Combine with previous data to build complete dataset
          const combinedData = [...prev];

          // Add new transactions if they don't already exist
          transactions.forEach(transaction => {
            if (!combinedData.some(item => item.id === transaction.id)) {
              combinedData.push(transaction);
            }
          });

          return combinedData;
        });
      } else {
        toast.error(`Error: ${data.message}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to fetch all data for export
  const fetchAllData = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=1&limit=1000`; // Large limit to get as much data as possible

    try {
      toast.loading('Preparing data...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      toast.dismiss();

      if (data.status === 'SUCCESS') {
        const allData = data.data;
        return isDateFilterActive ? applyDateFilter(allData) : allData;
      } else {
        toast.error(`Error: ${data.message}`);
        return [];
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Error: ${error.message}`);
      return [];
    }
  };

  useEffect(() => {
    getTopupHistory();
  }, [page, limit]);

  useEffect(() => {
    if (isDateFilterActive) {
      // Apply date filter to all data
      const filtered = applyDateFilter(allHistoryData);
      setFilteredData(filtered);

      // Update pagination with filtered data
      const paginatedData = filtered.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
      setTotalOrders(filtered.length);
    } else {
      // Reset to normal API-based pagination
      getTopupHistory();
    }
  }, [isDateFilterActive, startDate, endDate]);

  useEffect(() => {
    if (isDateFilterActive) {
      // When page or limit changes with active filter, update displayed data
      const paginatedData = filteredData.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
    }
  }, [page, limit, filteredData]);

  const applyDateFilter = (data) => {
    if (!startDate || !endDate) return data;

    const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
    const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);

    return data.filter(item => {
      const txnDate = new Date(item.txn_date).getTime();
      return txnDate >= startTimestamp && txnDate <= endTimestamp;
    });
  };

  const handleApplyDateFilter = () => {
    if (!startDate || !endDate) {
      toast.warning('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warning('Start date cannot be after end date');
      return;
    }

    setIsDateFilterActive(true);
    setPage(1); // Reset to first page when filter changes
  };

  const handleResetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsDateFilterActive(false);
    setPage(1);
  };

  // Function to convert JSON to CSV
  const convertToCSV = (data) => {
    const headers = role === 'admin'
      ? ['User ID', 'Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date']
      : ['Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date'];

    // Create CSV header row
    let csvContent = headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const values = role === 'admin'
        ? [
          `"${row.user_id || 'N/A'}"`,
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ]
        : [
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ];

      csvContent += values.join(',') + '\n';
    });

    return csvContent;
  };

  const downloadTopupHistory = async () => {
    try {
      toast.loading('Preparing download...');

      // Get all data for CSV export
      const dataToExport = await fetchAllData();

      if (dataToExport.length === 0) {
        toast.dismiss();
        toast.error('No data available to download');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(dataToExport);

      // Create downloadable file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `topup-history-${startDate || 'all'}-to-${endDate || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Download successful');
    } catch (error) {
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const renderTableHeaders = () => {
    const headers = [
      'Transaction ID',
      'Amount',
      'Last Balance',
      'Transaction Type',
      'Transaction Status',
      'Transaction Date',
      'Description',
    ];
    if (role === 'admin') headers.unshift('User ID');
    return headers;
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value));
    setPage(1);
  };


  const renderTableRow = (row, index) => (
    <tr
      key={row.id}
      className={`dark:bg-darkmodeColor dark:text-white hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
        }`}
    >
      {/* <td className="p-3">
        <input
          type="checkbox"
          checked={selectedRows.includes(index)}
          onChange={() => handleRowSelect(index)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
        />
      </td> */}
      {role === 'admin' && (
        <td className="p-2 text-xs text-gray-700 dark:text-white font-medium">
          {row.user_id}
        </td>
      )}
      <td className="relative group p-2 text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors">
        {row.trnx_id || "N/A"}
        <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-900 dark:bg-gray-800 text-white text-[10px] rounded-md px-2 py-1 shadow-lg z-10 whitespace-nowrap">
          💳 View transaction details here!
          <div className="absolute -top-1 left-3 w-1.5 h-1.5 bg-gray-900 dark:bg-gray-800 rotate-45"></div>
        </div>
      </td>
      <td className="p-2 text-xs text-gray-900 font-semibold dark:text-white">
        ₹{row.credit_amount || "N/A"}
      </td>
      <td className="p-2 text-xs text-gray-700 dark:text-white">
        ₹{row.closing_balance || "N/A"}
      </td>
      <td className="p-2 text-xs text-gray-700 dark:text-white capitalize">
        {row.payment_mode || "N/A"}
      </td>
      <td className="p-2">
        {row.status === 'add' ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700">
            Success
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
            {row.status}
          </span>
        )}
      </td>
      <td className="p-2 text-xs text-gray-700 dark:text-white">
        {row.txn_date || "N/A"}
      </td>
      <td className="p-2 text-xs text-gray-700 dark:text-white max-w-xs">
        <div
          className="truncate hover:text-clip hover:whitespace-normal cursor-help"
          title={row.txn_description || "No description available"}
        >
          {row.txn_description || "N/A"}
        </div>
      </td>
    </tr>
  );

  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'
        }`}
    >
      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: '113px', height: 'calc(100vh - 115px)', overflowY: 'auto' }
                : {}
            }
          >
            <Sidebar />
          </div>
        )}
        <div className="dark:bg-darkmodeColor w-full dark:text-white shadow-custom-light dark:shadow-custom-dark rounded-custom mt-4 mb-8 mr-4 ml-4 p-4 md:ml-4 md:mt-4 md:mb-8 md:mr-4 custom-scrollbar overflow-y-auto overflow-x-hidden">
          <button
            onClick={handleNavigation}
            className="text-xl sm:text-2xl font-bold ml-2 mt-2 sm:mr-4 bg-customPurple hover:bg-purple-700 dark:bg-customPurple dark:hover:bg-purple-700 text-white font-sans rounded h-[40px] w-[310px] sm:w-full flex items-center justify-center"
          >
            <FaCcVisa className="mr-2" size={24} />
            <FaCcMastercard className="mr-2" size={24} />
            TopUp History
          </button>

          {/* Date Picker Section */}
          <div className="mt-6 mx-2 p-6 border dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/30 shadow-sm">
            <h3 className="text-lg font-medium mb-4 dark:text-gray-200">Filter Transactions</h3>

            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              {/* Start Date Picker */}
              <div className="flex flex-col w-full md:w-auto">
                <label className="text-sm font-medium mb-2 dark:text-gray-300">Start Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 pr-10 w-full md:w-52 text-sm bg-white dark:bg-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                  <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                </div>
              </div>

              {/* End Date Picker */}
              <div className="flex flex-col w-full md:w-auto">
                <label className="text-sm font-medium mb-2 dark:text-gray-300">End Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 pr-10 w-full md:w-52 text-sm bg-white dark:bg-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                  <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                </div>
              </div>

              {/* Buttons Group */}
              <div className="flex flex-wrap gap-3 mt-4 md:mt-0 w-full md:w-auto md:ml-auto">
                <button
                  onClick={handleApplyDateFilter}
                  className="bg-customPurple hover:bg-purple-700 text-white font-medium rounded-lg px-5 py-3 text-sm flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0"
                >
                  <FaFilter className="mr-2" />
                  Apply Filter
                </button>

                {isDateFilterActive && (
                  <button
                    onClick={handleResetDateFilter}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg px-5 py-3 text-sm flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0"
                  >
                    <FaTimes className="mr-2" />
                    Reset
                  </button>
                )}

                <button
                  onClick={downloadTopupHistory}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg px-5 py-3 text-sm flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0"
                >
                  <FaDownload className="mr-2" />
                  Download
                </button>
              </div>
            </div>

            {/* Filter Status Message */}
            {isDateFilterActive && (
              <div className="mt-3 text-sm font-medium flex items-center text-green-600 dark:text-green-400">
                <FaCheckCircle className="mr-2" />
                Showing data from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
              </div>
            )}
          </div>

          <div className="overflow-x-auto ml-2 mt-4 mr-2">
            <table className="min-w-full table-auto custom-scrollbar overflow-y-auto dark:bg-darkmodeColor dark:text-white">
              <thead className='bg-customPurple text-white text-[10px] dark:bg-darkmodeColor dark:text-white'>
                <tr>
                  {/* <th className="p-2 text-left font-medium">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 ml-1 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th> */}
                  {renderTableHeaders().map((header, index) => (
                    <th key={index} className="p-2 text-left font-medium">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:bg-darkmodeColor dark:text-white">
                {historyData?.length > 0 ? (
                  historyData.map((row, index) => renderTableRow(row, index))
                ) : (
                  <tr>
                    <td colSpan={role === 'admin' ? "9" : "8"} className="text-center p-4 text-gray-500 dark:text-gray-400">
                      {isDateFilterActive ? 'No data available for the selected date range' : 'No data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <span className="text-sm text-gray-700 font-bold dark:text-gray-300">{`Total : ${totalOrders}`}</span>
            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className="border rounded p-1 sm:ml-[480px] text-sm bg-white dark:bg-darkmodeColor dark:text-gray-200"
            >
              {totalOrders === 0 ? (
                <option value={0}>0</option>
              ) : (
                <>
                  {totalOrders >= 10 && <option value={10}>10</option>}
                  {totalOrders >= 15 && <option value={15}>15</option>}
                  {totalOrders >= 20 && <option value={20}>20</option>}
                  {totalOrders >= 30 && <option value={30}>30</option>}
                  {totalOrders >= 30 && <option value={totalOrders}>All</option>}
                </>
              )}
            </select>

            <Pagination
              count={Math.ceil(totalOrders / limit)}
              page={page}
              onChange={handlePaginationChange}
              variant="outlined"
              shape="rounded"
              className="pagination"
              size="small"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  className={`mx-1 rounded-md ${item.selected ? 'bg-customPurple text-white' : 'text-gray-700 dark:text-gray-300'} hover:bg-purple-700 hover:text-white`}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default B2bTopupHistory;